import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { Button } from "@/components/ui/button";
import { FormMessage } from "@/components/ui/form-message";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AuthActionError,
  authErrorToTranslationKey,
  updatePassword,
} from "@/features/auth/api/authClient";
import {
  resetPasswordSchema,
  type ResetPasswordFormValues,
} from "@/features/auth/validation/authSchemas";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [message, setMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirm_password: "",
    },
  });

  async function onSubmit(values: ResetPasswordFormValues) {
    setMessage(null);
    setSubmitError(null);

    try {
      await updatePassword(values);
      setMessage(t("auth.resetPassword.success"));
      setTimeout(() => navigate("/login", { replace: true }), 800);
    } catch (error) {
      if (error instanceof AuthActionError) {
        setSubmitError(t(authErrorToTranslationKey(error.code)));
        return;
      }

      setSubmitError(t("auth.errors.generic"));
    }
  }

  return (
    <AuthLayout
      eyebrow={t("auth.meta.eyebrow")}
      title={t("auth.resetPassword.title")}
      subtitle={t("auth.resetPassword.subtitle")}
    >
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div>
          <Label htmlFor="reset-password">{t("auth.fields.newPassword")}</Label>
          <Input
            id="reset-password"
            type="password"
            autoComplete="new-password"
            {...form.register("password")}
          />
          {form.formState.errors.password ? (
            <FormMessage>{t(form.formState.errors.password.message ?? "auth.errors.generic")}</FormMessage>
          ) : null}
        </div>

        <div>
          <Label htmlFor="reset-confirm">{t("auth.fields.confirmPassword")}</Label>
          <Input
            id="reset-confirm"
            type="password"
            autoComplete="new-password"
            {...form.register("confirm_password")}
          />
          {form.formState.errors.confirm_password ? (
            <FormMessage>
              {t(form.formState.errors.confirm_password.message ?? "auth.errors.generic")}
            </FormMessage>
          ) : null}
        </div>

        {message ? <FormMessage className="text-olive">{message}</FormMessage> : null}
        {submitError ? <FormMessage>{submitError}</FormMessage> : null}

        <Button className="w-full" type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? t("common.saving") : t("auth.resetPassword.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
